import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { PlusCircle, Search, UserPlus, UserMinus, FileUp, Settings, X } from 'lucide-react';
import CompanyModal from './CompanyModal';
import CompanyList from './CompanyList';

// CompanyManagement component
const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  // Initial data fetch
  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedCompany(null);
  };

  const handleModalSave = () => {
    // Refresh company list after save
    fetchCompanies();
    handleModalClose();
  };


  // Fetch companies data
  const fetchCompanies = async () => {
    try {
      // Use our custom endpoint instead of the default WordPress one
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/companies`, {
        headers: {
          'X-WP-Nonce': boardMemberData.nonce
        }
      });
      const data = await response.json();
      setCompanies(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch companies');
      setLoading(false);
    }
};

  // Toggle company block status
  const handleToggleBlock = async (companyId) => {
    try {
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/companies/${companyId}/toggle-block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': boardMemberData.nonce
        }
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to toggle company status');
      }
  
      fetchCompanies(); // Refresh the list
    } catch (err) {
      setError(err.message || 'Failed to toggle company status');
    }
  };

  // Delete company
  const handleDelete = async (companyId) => {
    if (!window.confirm(__('Are you sure you want to delete this company?'))) {
      return;
    }

    try {
      const response = await fetch(`${boardMemberData.restURL}wp/v2/company/${companyId}`, {
        method: 'DELETE',
        headers: {
          'X-WP-Nonce': boardMemberData.nonce
        }
      });

      if (response.ok) {
        fetchCompanies();
      }
    } catch (err) {
      setError('Failed to delete company');
    }
  };

  // Filter companies based on search
  const filteredCompanies = companies.filter(company => 
    company.title.rendered.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render company list
  // const CompanyList = () => (
  //   <div className="overflow-x-auto">
  //     <table className="min-w-full divide-y divide-gray-200">
  //       <thead className="bg-gray-50">
  //         <tr>
  //           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
  //             {__('Company Name')}
  //           </th>
  //           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
  //             {__('Status')}
  //           </th>
  //           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
  //             {__('Actions')}
  //           </th>
  //         </tr>
  //       </thead>
  //       <tbody className="bg-white divide-y divide-gray-200">
  //         {filteredCompanies.map(company => (
  //           <tr key={company.id}>
  //             <td className="px-6 py-4 whitespace-nowrap">
  //               {company.title.rendered}
  //             </td>
  //             <td className="px-6 py-4 whitespace-nowrap">
  //               <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
  //                 company.meta._account_status === 'blocked' 
  //                   ? 'bg-red-100 text-red-800' 
  //                   : 'bg-green-100 text-green-800'
  //               }`}>
  //                 {company.meta._account_status === 'blocked' ? __('Blocked') : __('Active')}
  //               </span>
  //             </td>
  //             <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
  //               <button
  //                 onClick={() => {
  //                   setSelectedCompany(company);
  //                   setShowEditForm(true);
  //                 }}
  //                 className="text-indigo-600 hover:text-indigo-900"
  //               >
  //                 <Settings className="h-5 w-5" />
  //               </button>
  //               <button
  //                 onClick={() => toggleCompanyBlock(company.id)}
  //                 className="text-yellow-600 hover:text-yellow-900"
  //               >
  //                 {company.meta._account_status === 'blocked' ? 
  //                   <UserPlus className="h-5 w-5" /> : 
  //                   <UserMinus className="h-5 w-5" />
  //                 }
  //               </button>
  //               <button
  //                 onClick={() => deleteCompany(company.id)}
  //                 className="text-red-600 hover:text-red-900"
  //               >
  //                 <X className="h-5 w-5" />
  //               </button>
  //             </td>
  //           </tr>
  //         ))}
  //       </tbody>
  //     </table>
  //   </div>
  // );

  // Only render for administrators
  if (!boardMemberData.isAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">{__('Company Management')}</h1>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            {__('Add Company')}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mt-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder={__('Search companies...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="mt-4 flex justify-center">
          <div className="loader">{__('Loading...')}</div>
        </div>
      ) : (
        <div className="mt-8">
         <CompanyList
            companies={companies}
            onToggleBlock={handleToggleBlock}
            onDelete={handleDelete}
            onEdit={(company) => {
              setSelectedCompany(company);
              setShowAddForm(true);
            }}
            searchTerm={searchTerm}
          />
        </div>
      )}

      {/* Add/Edit Company Modal - This would be a separate component */}
      {(showAddForm || showEditForm) && (
        <CompanyModal
          company={selectedCompany}
          onClose={() => {
            setShowAddForm(false);
            setShowEditForm(false);
            setSelectedCompany(null);
          }}
          onSave={() => {
            fetchCompanies();
            setShowAddForm(false);
            setShowEditForm(false);
            setSelectedCompany(null);
          }}
        />
      )}
      {showModal && (
        <CompanyModal
          company={selectedCompany}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};


export default CompanyManagement;